import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { vehicleAPI } from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { formatDwellingUnit } from '../../utils/brandingUtils';
import PageTitle from '../shared/PageTitle';

const GuardVehicles = () => {
  const { currentUser } = useContext(AuthContext);
  const { settings } = useSettings();
  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Get dwelling unit name
  const unitSingular = formatDwellingUnit(settings);

  useEffect(() => {
    fetchVehicles();
  }, []);

  // Apply filters whenever search term, filter type, or vehicles change
  useEffect(() => {
    let filtered = [...vehicles];

    // Filter by vehicle type
    if (filterType !== 'all') {
      filtered = filtered.filter(vehicle => vehicle.vehicle_type === filterType);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(vehicle =>
        vehicle.registration_number?.toLowerCase().includes(searchLower) ||
        vehicle.make?.toLowerCase().includes(searchLower) ||
        vehicle.model?.toLowerCase().includes(searchLower) ||
        vehicle.color?.toLowerCase().includes(searchLower) ||
        vehicle.villa_number?.toLowerCase().includes(searchLower) ||
        vehicle.owner_name?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredVehicles(filtered);
  }, [searchTerm, filterType, vehicles]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const res = await vehicleAPI.getOrgVehicles();
      setVehicles(res.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching vehicles for guard:', err);
      setError('Failed to load vehicles.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getVehicleTypeBadge = (type) => {
    switch (type) {
      case '2-wheeler':
        return <span className="badge bg-info">2-Wheeler</span>;
      case '4-wheeler':
        return <span className="badge bg-primary">4-Wheeler</span>;
      default:
        return <span className="badge bg-secondary">{type}</span>;
    }
  };

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <PageTitle icon="fa-car" title="Vehicle Management" />
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading vehicles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      <PageTitle
        icon="fa-car"
        title="Vehicle Management"
        action={
          <Link to="/guard/dashboard" className="btn btn-outline-secondary">
            <i className="fas fa-arrow-left me-2"></i>Back to Dashboard
          </Link>
        }
      />
      <p className="text-muted mb-4">All vehicles registered in your organization</p>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by registration, make, model, color, unit, or owner..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                  >
                    <option value="all">All Vehicle Types</option>
                    <option value="2 wheeler">2-Wheeler</option>
                    <option value="4 wheeler">4-Wheeler</option>
                  </select>
                </div>
                
                <div className="col-md-3">
                  <div className="d-flex align-items-center justify-content-end">
                    <span className="badge bg-primary me-2">
                      {filteredVehicles.length} vehicle{filteredVehicles.length !== 1 ? 's' : ''}
                    </span>
                    <button 
                      className="btn btn-outline-secondary btn-sm searchCloseFilter"
                      onClick={() => {
                        setSearchTerm('');
                        setFilterType('all');
                      }}
                      title="Clear filters"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicles Table */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-car me-2"></i>
            Registered Vehicles
          </h5>
          <span className="text-muted">
            Organization: {currentUser?.organization_name || 'Current Organization'}
          </span>
        </div>
        <div className="card-body">
          {filteredVehicles.length === 0 ? (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              {searchTerm || filterType !== 'all' 
                ? 'No vehicles found matching your filters.' 
                : 'No vehicles registered in your organization.'}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Type</th>
                    <th>{unitSingular}</th>
                    <th>Owner</th>
                    <th>Registration</th>
                    <th>Vehicle Details</th>
                    <th>Color</th>
                    <th>Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVehicles.map(vehicle => (
                    <tr key={vehicle.id}>
                      <td>#{vehicle.id}</td>
                      <td>{getVehicleTypeBadge(vehicle.vehicle_type)}</td>
                      <td>
                        <strong>{vehicle.villa_number}</strong>
                      </td>
                      <td>
                        <div>
                          <strong>{vehicle.owner_name}</strong>
                        </div>
                      </td>
                      <td>
                        <code className="bg-light px-2 py-1 rounded">
                          {vehicle.registration_number}
                        </code>
                      </td>
                      <td>
                        <div>
                          <strong>{vehicle.make}</strong>
                          {vehicle.model && (
                            <div className="text-muted small">{vehicle.model}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {vehicle.color}
                        </span>
                      </td>
                      <td>
                        <div className="text-muted small">
                          {formatDate(vehicle.created_at)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {filteredVehicles.length > 0 && (
          <div className="card-footer bg-light">
            <div className="row align-items-center">
              <div className="col">
                <small className="text-muted">
                  Showing {filteredVehicles.length} of {vehicles.length} total vehicles
                </small>
              </div>
              <div className="col-auto">
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={fetchVehicles}
                  title="Refresh data"
                >
                  <i className="fas fa-sync-alt me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuardVehicles; 